// PlannerProvider
/* eslint indent: 0, standard/no-callback-literal:0, react/jsx-handler-names:0, no-unmodified-loop-condition: 0 */

import React from 'react'
import _ from 'lodash'
import { DragDropContext as dragDropContext } from 'react-dnd'
import HTML5Backend from 'react-dnd-html5-backend'

import app from '../app.jsx'
import Acl from './acl.jsx'
import filterUtil from './filterutil.jsx'
import TaskUtil from './taskutil.jsx'
import TimeUtil from './timeutil.jsx'
import IntlUtil from './intlutil.jsx'
import DateUtil from './dateutil.jsx'
import DialogTimeCardDetail from '../time/dialog/dialog_timecard_detail/dialog_timecard_detail.jsx'
import SidebarTask from '../theme/layout/sidebar_task/sidebar_task.jsx'
import Grid from '../theme/grid/grid/grid.jsx'
import GridHeader from '../theme/grid/grid_header/grid_header.jsx'
import GridFilter from '../theme/grid/grid_filter/grid_filter.jsx'
import { WithDragSource as PlannerTaskItem } from '../theme/task/task_item/task_item.jsx'
import IconButton from '../theme/common/icon_button/icon_button.jsx'
import PlannerContact from '../theme/planner/planner_contact/planner_contact.jsx'
import PlannerProject from '../theme/planner/planner_project/planner_project.jsx'
import PlannerPhase from '../theme/planner/planner_phase/planner_phase.jsx'
import PlannerActivity from '../theme/planner/planner_activity/planner_activity.jsx'
import PlannerTask from '../theme/planner/planner_task/planner_task.jsx'
import PlannerUser from '../theme/planner/planner_user/planner_user.jsx'
import PlannerHeader from '../theme/planner/planner_header/planner_header.jsx'
import SidebarPhase from '../theme/layout/sidebar_phase/sidebar_phase.jsx'
import SidebarActivity from '../theme/layout/sidebar_activity/sidebar_activity.jsx'
import PlannerActionGroup from '../theme/planner/planner_actiongroup/planner_actiongroup.jsx'
import DialogConfirm from '../theme/dialog/dialog_confirm/dialog_confirm.jsx'
import timerStates from '../../shared/util/timer/timerStates.js'
import { buildTimerEvents } from '../../shared/util/timer/timerEvents.js'
import { TASK_PRIORITIES } from '../../shared/constants.js'

const PlannerPageProvider = BaseClass =>
  @dragDropContext(HTML5Backend)
  class extends BaseClass {
    /* Common Actions functions - START */
    _renderCommonActions () {
      return (
        <div className='commonactions'>
          <IconButton
            size='big'
            icon='+'
            className='detailadd'
            type='success'
            onClick={this._onAddTask.bind(this, null)}
          />
        </div>
      )
    }

    _onAddTask (activityId) {
      this.data.currentEditObject = null
      this.showRightSidebar()

      this._triggerAddTask(activityId, 1)
    }

    _triggerAddTask (activityId, noTries) {
      setTimeout(() => {
        if (this._references.sidebarTask.current) {
          const { project, pageState, context, projects } = this.data
          let projectId = null
          const projectOptions =
            (context ? context.projectOptions : projects) || []

          if (project) {
            projectId = project.id
          } else if (
            pageState.filter &&
            pageState.filter.projectIds &&
            pageState.filter.projectIds.length === 1
          ) {
            projectId = pageState.filter.projectIds[0]
          } else if (projectOptions && projectOptions.length === 1) {
            projectId = projectOptions[0].value
          }

          const task = {
            projectId: projectId,
            activityId: activityId,
            priority: TASK_PRIORITIES.trivial
          }
          this._references.sidebarTask.current.showAddTask(task)
        } else if (noTries <= 5) {
          this._triggerAddTask(activityId, noTries + 1)
        }
      }, 100)
    }

    /* Common Actions functions - END */

    /* Task functions - START */
    _onUpdateTaskInfo (taskId, updateData) {
      if (!taskId) {
        app.debug('BasePlannerPage::_onUpdateTaskInfo - Invalid task id')
        return
      }

      app
        .service('/api/project/plannertask')
        .patch(taskId, updateData)
        .then(() => {})
        .catch(err => {
          app.debug('BasePlannerPage::_onUpdateTaskInfo - Error ', err)
        })
    }

    _getTaskScheduleDate () {
      if (this.activeDate !== undefined && this.activeDate) {
        const currDate = this.activeDate
        return new Date(
          currDate.getFullYear(),
          currDate.getMonth(),
          currDate.getDate()
        )
      }
      return null
    }

    _onCreateTask (createData) {
      if (!createData && !createData.title) {
        app.debug('BasePlannerPage::_onCreateTask - Invalid task id')
        return
      }

      createData.createdById = app.loggedInId
      createData.active = true

      const schedules = []

      const scheduleDate = this._getTaskScheduleDate()
      if (scheduleDate) {
        schedules.push({
          date: scheduleDate,
          noHours: 0
        })
      } else {
        const currDate = new Date()
        schedules.push({
          date: new Date(
            currDate.getFullYear(),
            currDate.getMonth(),
            currDate.getDate()
          ),
          noHours: 0
        })
      }
      createData.schedules = schedules

      app
        .service('/api/project/plannertask')
        .create(createData)
        .then(createRes => {
          if (this._references.sidebarTask.current) {
            this._references.sidebarTask.current.showEditTask(createRes)
          }
        })
        .catch(err => {
          app.debug('BasePlannerPage::_onCreateTask - Error ', err)
        })
    }

    _onDeleteTask (taskId) {
      if (!taskId) {
        app.debug('BasePlannerPage::_onDeleteTask - Invalid task id')
        return
      }

      this.data.currentEditObject = null
      this.hideRightSidebar()

      app
        .service('/api/project/plannertask')
        .remove(taskId)
        .then(() => {})
        .catch(err => {
          app.debug('BasePlannerPage::_onDeleteTask - Error ', err)
        })
    }

    _onUpdateDocuments (uploadData) {
      app
        .service('/api/project/plannertaskdoc')
        .create(uploadData)
        .then(() => {})
        .catch(err => {
          app.debug('BasePlannerPage::_onUpdateDocuments - Error ', err)
        })
    }

    _onUploadError = (file, message) => {
      if (this._references.uploadErrorDialog.current) {
        let messageInfo = ''

        if (message.startsWith('File is too big')) {
          messageInfo = IntlUtil.convertMessage(
            'plannerv2.task.errorDocFileSize',
            { name: file.name }
          )
        } else if (message.startsWith("You can't upload files of this type")) {
          messageInfo = IntlUtil.convertMessage(
            'plannerv2.task.errorDocFileFormat',
            { name: file.name }
          )
        }

        this._references.uploadErrorDialog.current.show(messageInfo)
      }
    }

    _onCreateComment = (task, commentData) => {
      if (!task.id) {
        return
      }

      const user = app.loggedInUserTeam
      commentData.userId = app.loggedInId
      commentData.user = {
        displayName: user.displayName,
        imageUrl: user.imageUrl,
        userId: user.userId
      }

      task.comments = task.comments || []
      task.comments.unshift(commentData)

      const updateData = {
        comments: task.comments.map(comment => {
          const { content, userId, createdOn } = comment
          return { content, userId, createdOn }
        })
      }

      app
        .service('/api/project/plannertask')
        .patch(task.id, updateData)
        .then(() => {})
        .catch(err => {
          app.debug('BasePlannerPage::_onCreateComment - Error ', err)
        })
    }
    /* Task functions - END */

    /* Activity functions - START */
    _onUpdateActivityInfo (activityId, updateData) {
      if (!activityId) {
        app.debug(
          'BasePlannerPage::_onUpdateActivityInfo - Invalid activity id'
        )
        return
      }

      app
        .service('/api/project/budgetitem')
        .patch(activityId, updateData)
        .then(() => {})
        .catch(err => {
          app.debug('BasePlannerPage::_onUpdateActivityInfo - Error ', err)
        })
    }
    /* Activity functions - END */

    /* Phase functions - START */
    _onUpdatePhaseInfo (projectId, phaseNo, updateData) {
      if (!projectId) {
        app.debug('BasePlannerPage::_onUpdatePhaseInfo - Invalid project')
        return
      }

      app
        .service('/api/project')
        .get(projectId)
        .then(projectRes => {
          let newPhase = projectRes.budget.phases[phaseNo - 1]
          if (newPhase) {
            newPhase = Object.assign(newPhase, updateData)

            return app
              .service('/api/project')
              .patch(projectRes.id, { budget: projectRes.budget })
          }
        })
        .catch(err => {
          app.debug('BasePlannerPage::_onUpdatePhaseInfo - Error ', err)
        })
    }
    /* Phase functions - END */

    /* Task functions - START */
    _onChangePriority (task, priority) {
      this._onUpdateTaskInfo(task.id, { priority: priority })
      this._updateTaskInfoInRightSidebar('priority', priority)
    }

    _onToggleCompleted (task, bCompleted) {
      const plannedDate = this._getTaskScheduleDate()

      this._onUpdateTaskInfo(task.id, { completed: bCompleted || false })
      this._updateTaskInfoInRightSidebar('completed', bCompleted)

      if (task.userId && bCompleted) {
        const timeCard = TaskUtil.getTimecardDataFromTask(
          task,
          true,
          false,
          plannedDate
        )
        TimeUtil.createTimeCard(timeCard, createRes => {
          app.changePage('time', 'showTimeCard', createRes.id, {
            userId: createRes.userId,
            date: null
          })
        })
      }
    }

    _onCompleteSubtask (task, subtaskIndex, bCompleted) {
      const subtasks = task.subtasks
      subtasks[subtaskIndex].completed = bCompleted
      const updateData = { subtasks }

      this._onUpdateTaskInfo(task.id, updateData)
    }

    _updateTaskInfoInRightSidebar (name, value) {
      if (this.isRightSidebarOpen() && this._references.sidebarTask.current) {
        this._references.sidebarTask.current.updateTaskInfo(name, value)
      }
    }

    _onSaveHistory () {
      const { pageState } = this.data
      const keyword =
        pageState && pageState.filter ? pageState.filter.keyword : ''

      this._updateGridState(
        this._pageIdent,
        keyword,
        pageState.filters.filter(obj => !obj.hideDelete),
        () => {
          // After Create/Update grid state, reload the histories
          this._reloadGridState(gridState => {
            this.data.histories = gridState.histories || []
            // this.setState({ dataDate: new Date() });
          })
        }
      )
    }

    _onClearHistory () {
      this.data.histories = []
      this._clearGridState(this._pageIdent)

      this.setState({ dataDate: new Date() })
    }

    _onSelectHistory (history) {
      const defaultFilters = [...this.data.pageState.filters]
      _.remove(defaultFilters, obj => !obj.hideDelete)

      this._onChangeFilter(history.keyword, [
        ...defaultFilters,
        ...history.filters
      ])
    }

    _onToggleToolbarActions = () => {
      const { pageState } = this.data
      pageState.showToolbarActions = !pageState.showToolbarActions
      this.setState({ dataDate: new Date() })

      this._updatePageState(pageState)
    }

    _getFilterTypes () {
      const showProject =
        this._pageIdent === 'planner' ||
        this._pageIdent === 'plannertask' ||
        this._pageIdent === 'task'

      const filterTypes = [
        {
          icon: 'search',
          ident: 'keyword',
          labelIdent: IntlUtil.convertMessage(
            'common.newfilter.lableIdentSearch'
          ),
          labelValue: ''
        },
        {
          icon: 'assignee',
          ident: 'assignee',
          labelIdent: IntlUtil.convertMessage(
            'common.newfilter.lableIdentAssignee'
          ),
          labelValue: IntlUtil.convertMessage(
            'common.newfilter.lableValueAssignee'
          ),
          isHidden: Acl.isStandardUser()
        },
        {
          icon: 'client',
          ident: 'contact',
          labelIdent: IntlUtil.convertMessage(
            'common.newfilter.lableIdentContact'
          ),
          labelValue: IntlUtil.convertMessage(
            'common.newfilter.lableValueContact'
          ),
          isHidden: !showProject
        },
        {
          icon: 'duedate',
          ident: 'duedate',
          labelIdent: IntlUtil.convertMessage(
            'common.newfilter.lableIdentDueDate'
          ),
          labelValue: IntlUtil.convertMessage(
            'common.newfilter.lableValueDueDate'
          )
        },
        {
          icon: 'project',
          ident: 'project',
          labelIdent: IntlUtil.convertMessage(
            'common.newfilter.lableIdentProject'
          ),
          labelValue: IntlUtil.convertMessage(
            'common.newfilter.lableValueProject'
          ),
          isHidden: !showProject
        },
        {
          icon: 'tag',
          ident: 'tag',
          labelIdent: IntlUtil.convertMessage('common.newfilter.lableIdentTag'),
          labelValue: IntlUtil.convertMessage('common.newfilter.lableValueTag'),
          isHidden: !showProject
        },
        {
          icon: 'success',
          ident: 'status',
          labelIdent: IntlUtil.convertMessage(
            'common.newfilter.lableIdentStatus'
          ),
          labelValue: IntlUtil.convertMessage(
            'common.newfilter.lableValueStatus'
          )
        }
      ]

      return filterTypes
    }

    _onChangeFilter (keyword, filters) {
      const projectIds = []
      const contactIds = []
      const userIds = []
      const tags = []
      const status = []

      filters.forEach(filter => {
        if (filter.ident === 'project') {
          projectIds.push(filter.value)
        } else if (filter.ident === 'contact') {
          contactIds.push(filter.value)
        } else if (filter.ident === 'assignee') {
          userIds.push(filter.value)
        } else if (filter.ident === 'tag') {
          tags.push(filter.value)
        } else if (filter.ident === 'status') {
          status.push(filter.value)
        }
      })

      // Whenever filter is changed, then results will change - so clear the expand states
      this.data.pageState.expandObjectIds = null
      this.data.pageState.filters = filters
      this.data.pageState.filter = {
        keyword: '',
        projectIds,
        contactIds,
        userIds,
        tags,
        status
      }

      this._updatePageState(this.data.pageState, () => {
        this._reloadData()
      })
    }

    _onEditTask (task, isViewMode) {
      if (task.plannedHours === undefined) {
        task.plannedHours = 0
        if (task.schedules) {
          task.schedules.forEach(schedule => {
            task.plannedHours += schedule.noHours || 0
          })
        }
      }

      this.data.currentEditObject = task
      this.showRightSidebar()

      this._triggerEditTask(task, isViewMode, 1)
    }

    _triggerEditTask (task, isViewMode, noTries) {
      setTimeout(() => {
        if (this._references.sidebarTask.current) {
          this._references.sidebarTask.current.showEditTask(task, isViewMode)
        } else if (noTries <= 5) {
          this._triggerEditTask(task, isViewMode, noTries + 1)
        }
      }, 100)
    }

    _onAddTime (task) {
      const plannedDate = this._getTaskScheduleDate()

      if (task.userId) {
        const timeCard = TaskUtil.getTimecardDataFromTask(
          task,
          false,
          true,
          plannedDate
        )
        TimeUtil.createTimeCard(timeCard, createRes => {
          app.changePage('time', 'showTimeCard', createRes.id, {
            userId: createRes.userId,
            date: null
          })
        })
      }
    }

    _onStart (task) {
      const timer = this._getTimer()
      if (timer.exists && !timer.ProjectId()) {
        app
          .service('/api/timecard')
          .get(timer.TimeCardId(), {})
          .then(result => {
            if (result) {
              const timecard = result
              if (this._references.dialogTimeCardDetail.current) {
                this._references.dialogTimeCardDetail.current.show(
                  timecard,
                  true,
                  false,
                  !timer.TaskId()
                )
              }
              this.data.activeTask = task
            }
          })
          .catch(err => {
            app.debug('PageTime: _getDataTimeCardStat err', err)
          })
      } else {
        this._createOrUpdateTask(task)
      }
    }

    _onPause (_task) {
      const timer = this._getTimer()
      timer.pause(app)
    }

    _onResume (_task) {
      const timer = this._getTimer()
      timer.resume(app)
    }

    _onStop (_task) {
      const timer = this._getTimer()
      timer.stop(app)
    }

    _createOrUpdateTask (task) {
      const timer = this._getTimer()
      const timerEvents = buildTimerEvents(app)
      if (timer.doesRelateToTaskId(task.id)) {
        timer.resume(app)
      } else {
        const createObj = {
          userId: task.userId,
          projectId: task.projectId,
          activityId: task.activityId,
          taskId: task.id,
          timeCardId: null,
          status: timerStates.STARTED
        }

        timerEvents.createTimer(createObj)
      }
    }

    _onUpdateTimeCardDetail (bFinalize, timeCardId, detailObj, bStopTimer) {
      if (this._references.dialogTimeCardDetail.current) {
        this._references.dialogTimeCardDetail.current.hide()
      }

      const timer = this._getTimer()
      TimeUtil.updateTimeCardDetail(timeCardId, detailObj, timer, bStopTimer)

      // Complete task on finalize
      if (bFinalize && detailObj.taskId) {
        TimeUtil.onCompleteTask(detailObj.taskId)
      }
    }

    _onDeleteTimeCardDetail (timeCardId) {
      if (!timeCardId) {
        return false
      }

      // Remove timecard and remove timer
      this._removeTimerAndTimeCard(timeCardId, () => {
        if (this._references.dialogTimeCardDetail.current) {
          this._references.dialogTimeCardDetail.current.hide()
        }
      })
    }

    _renderTaskContent () {
      const { tasks } = this.data

      return (
        <div className='pagecontentwrap'>
          {this._renderGridFilter()}
          <Grid>
            {this._renderTaskGridHeader(false)}
            <div className='cgrid-content tasklist'>
              {tasks.map(task => this._renderTaskItem(task))}
            </div>
          </Grid>
          {this._renderTimecardDetail()}
          {this._renderDialogConfirm()}
        </div>
      )
    }

    _renderGridFilter () {
      const { pageState, histories } = this.data

      return (
        <GridFilter
          keyword={pageState.filter ? pageState.filter.keyword : ''}
          filters={pageState.filters || []}
          histories={histories || []}
          filterTypes={this._getFilterTypes()}
          isExpand={pageState.showToolbarActions}
          onSearchObjects={this._onSearchObjects.bind(this)}
          onChange={this._onChangeFilter.bind(this)}
          onSaveHistory={this._onSaveHistory.bind(this)}
          onClearHistory={this._onClearHistory.bind(this)}
          onSelectHistory={this._onSelectHistory.bind(this)}
          onToggleExpand={this._onToggleToolbarActions}
        />
      )
    }

    _renderTaskGridHeader () {
      const { currentEditObject } = this.data

      const columns = [
        {
          ident: 'all',
          title: 'plannerv2.task.gridHeaderAll',
          style: { minWidth: '70px' }
        },
        {
          ident: 'title',
          title: 'plannerv2.task.gridHeaderTitle',
          style: { minWidth: 100, width: '18%', textAlign: 'left' }
        },
        {
          ident: 'description',
          title: 'plannerv2.task.gridHeaderDescription',
          style: { minWidth: 90, width: '14%', textAlign: 'left' }
        },
        {
          ident: 'projectName',
          title: 'plannerv2.task.gridHeaderProjectName',
          style: { minWidth: 50, width: '10%', textAlign: 'left' }
        },
        {
          ident: 'client',
          title: 'plannerv2.task.gridHeaderClient',
          style: { minWidth: 50, width: '10%', textAlign: 'center' }
        },
        {
          ident: 'dueDate',
          title: 'plannerv2.task.gridHeaderDueDate',
          style: { minWidth: 40, width: '8%', textAlign: 'center' }
        },
        {
          ident: 'assignee',
          title: 'plannerv2.task.gridHeaderAssignee',
          style: { minWidth: 30, width: '6%', textAlign: 'center' }
        },
        {
          ident: 'hour',
          title: 'plannerv2.task.gridHeaderHour',
          style: { minWidth: 25, width: '5%', textAlign: 'center' }
        },
        {
          ident: 'budget',
          title: 'plannerv2.task.gridHeaderBudget',
          style: { minWidth: 25, width: '5%', textAlign: 'center' }
        },
        {
          ident: 'start',
          title: 'plannerv2.task.gridHeaderStart',
          style: { minWidth: 40, width: '7%', textAlign: 'center' }
        },
        {
          ident: 'priority',
          title: 'plannerv2.task.gridHeaderPriority',
          style: { minWidth: 25, width: '3%', textAlign: 'center' }
        },
        ...(currentEditObject
          ? []
          : [
              {
                ident: 'status',
                title: 'plannerv2.task.gridHeaderStatus',
                style: { minWidth: 64, width: '7%', textAlign: 'center' }
              }
            ])
      ]

      return <GridHeader columns={columns} showSelectAll />
    }

    _renderTimecardDetail () {
      const { projects } = this.data
      return (
        <DialogTimeCardDetail
          ref={this._references.dialogTimeCardDetail}
          projects={projects}
          getActivities={TimeUtil.onSearchActivity}
          getTasks={TimeUtil.onGetTasks}
          onUpdateDetail={this._onUpdateTimeCardDetail.bind(this, false)}
          onFinalize={this._onUpdateTimeCardDetail.bind(this, true)}
          onDelete={this._onDeleteTimeCardDetail.bind(this)}
        />
      )
    }

    _onSearchObjects (ident, keyword, existingIds, callback) {
      const query = {}
      const { pageState } = this.data

      // If any contact filter is there, search projects only for that contacts
      if (
        ident === 'project' &&
        pageState &&
        pageState.filter &&
        pageState.filter.contactIds &&
        pageState.filter.contactIds.length > 0
      ) {
        query.contactId = pageState.filter.contactIds
      }

      filterUtil.onSearchObjects(ident, keyword, existingIds, query, callback)
    }

    _renderTaskItem (
      task,
      canDrag = false,
      showVisibleOption = false,
      showPlannedHours = false
    ) {
      const { currentEditObject, showProjectInfo, project } = this.data
      const timer = this._getTimer()
      const bUserAssignee = task.userId === app.loggedInId
      let showTimecardAdd = false
      let showStart = false
      let showPause = false
      let showResume = false
      let dragEnable = false

      /*
      ## Testcases to show time + button
        - Task must have assignee, project, activity
        - There should not be any realtime timer running for logged in user
        - Logged in user must be either Project Manager or Assignee of that task
    */
      if (
        task.userId &&
        task.projectId &&
        task.activityId &&
        (
          timer.isStoppable() ||
          timer.isResumable() ||
          !timer.doesRelateToTaskId(task.id))
      ) {
        if (Acl.isRoleProjectManager() || bUserAssignee) {
          showTimecardAdd = true
        }
      }

      if (bUserAssignee) {
        /*
        ## Testcases to show pause button
          - For current task there should be a realtime timer running for logged in user
      */
        if (timer.isPauseable() && timer.doesRelateToTaskId(task.id)) {
          showPause = true

          /*
        ## Testcases to show resume button
          - For current task there should be a realtime timer paused for logged in user
      */
        } else if (timer.isResumable() && timer.doesRelateToTaskId(task.id)) {
          showResume = true

          /*
        ## Testcases to show start button
          - There should not be any realtime timer running for logged in user
          - Logged in user must be Assignee of that task
      */
        } else if (
          timer.isStartable() ||
          !timer.doesRelateToTaskId(task.id)
        ) {
          showStart = true
        }
      }

      if (
        project &&
        (project.budget.phases.length > 1 ||
          project.budget.noActiveTimeActivities > 1)
      ) {
        dragEnable = true
      }

      const plannedDate = this._getTaskScheduleDate()

      const bTaskExpand = !!(
        this.data.pageState.expandObjectIds &&
        this.data.pageState.expandObjectIds.indexOf(task.id) >= 0
      )

      return (
        <PlannerTaskItem
          activityIsActive={task.activityIsActive}
          key={task.id}
          task={task}
          timer={timer}
          active={currentEditObject && currentEditObject.id === task.id}
          showTimecardAdd={showTimecardAdd}
          showStart={showStart}
          showPause={showPause}
          showResume={showResume}
          canDrag={canDrag}
          showVisibleOption={showVisibleOption}
          hideActions={!!currentEditObject}
          onEdit={this._onEditTask.bind(this, task, false)}
          onChangePriority={this._onChangePriority.bind(this, task)}
          onToggleCompleted={this._onToggleCompleted.bind(this, task)}
          onCompleteSubtask={this._onCompleteSubtask.bind(this, task)}
          onAddTime={this._onAddTime.bind(this, task)}
          onStart={this._onStart.bind(this, task)}
          onPause={this._onPause.bind(this, task)}
          onResume={this._onResume.bind(this, task)}
          stopAndCreateNewTimeCard={this._stopAndCreateNewTimeCard.bind(this)}
          showProjectInfo={showProjectInfo}
          onToggleVisibility={this._onEditTask.bind(this, task, true)}
          dragEnable={dragEnable}
          isExpanded={bTaskExpand}
          onCollapse={this._onTaskCollapse}
          plannedDate={plannedDate}
          showPlannedHours={showPlannedHours}
        />
      )
    }
    /* Task functions - END */

    _onTaskCollapse = (bTaskExpand, objectId) => {
      if (!this.data.pageState.expandObjectIds) {
        this.data.pageState.expandObjectIds = []
      }

      if (bTaskExpand) {
        this.data.pageState.expandObjectIds.push(objectId)
      } else {
        _.remove(this.data.pageState.expandObjectIds, id => id === objectId)
      }

      this.setState({ dataDate: new Date() })

      this._updatePageState(this.data.pageState)
    }

    /* Planner functions - START */
    _renderPlannerContent () {
      const {
        pageState,
        dayItems,
        weekItems,
        fromDate,
        toDate,
        kanbanInfo,
        taskFromDate,
        taskToDate
      } = this.data

      return (
        <div className='pagecontentwrap'>
          {this._renderGridFilter()}
          {this._renderPlannerActionGroup()}
          <PlannerHeader
            key={pageState.viewMode}
            viewMode={pageState.viewMode}
            dayItems={dayItems}
            weekItems={weekItems}
            fromDate={pageState.viewMode === 'kanban' ? taskFromDate : fromDate}
            toDate={pageState.viewMode === 'kanban' ? taskToDate : toDate}
            kanbanInfo={kanbanInfo}
            onClickPrevious={this._onNavigate.bind(this, -1)}
            onClickNext={this._onNavigate.bind(this, 1)}
            onChangeMode={this._onChangeMode.bind(this)}
            onChangeDate={this._onChangeDate.bind(this)}
          />
          <div className='plannercontent'>{this._renderPlannerObjects()}</div>
          {this._renderDialogConfirm()}
        </div>
      )
    }

    _renderPlannerObjects () {
      const { objects, pageState } = this.data

      this.data.showLoader = false
      if (objects.length === 0) {
        return null
      }

      // Get the first leven items to be displayed and render based on pageNo
      let parentObjects = objects
      let topLevelObjects = null
      const level = 1
      // For user no need to check for sub objects
      let objectType =
        objects.length > 0 && objects[0].type === 'user' ? 'user' : 'contact'
      do {
        const isVisisble = pageState.visibleObjects.indexOf(objectType) >= 0
        if (isVisisble) {
          topLevelObjects = parentObjects
        } else {
          if (objectType === 'contact') {
            objectType = 'project'
            parentObjects = this._getObjectSubObjects(parentObjects, 'projects')
          } else if (objectType === 'project') {
            objectType = 'phase'
            parentObjects = this._getObjectSubObjects(parentObjects, 'phases')
          } else if (objectType === 'phase') {
            objectType = 'activity'
            parentObjects = this._getObjectSubObjects(
              parentObjects,
              'activities'
            )
          } else if (objectType === 'activity') {
            objectType = 'task'
            parentObjects = this._getObjectSubObjects(parentObjects, 'tasks')
          } else {
            parentObjects = []
          }
        }
      } while (topLevelObjects === null && parentObjects.length > 0)

      if (!topLevelObjects) {
        return null
      }

      const rowsPerPage = 20
      const { pageNo } = this.data
      const totalPages = Math.ceil(topLevelObjects.length / 20)
      if (totalPages > 1) {
        if (pageNo < totalPages) {
          this.data.showLoader = true
        }

        topLevelObjects = _.slice(topLevelObjects, 0, pageNo * rowsPerPage - 1)
      }

      return topLevelObjects.map(obj =>
        this._renderObjectItem(obj, pageState.visibleObjects, level)
      )
    }

    _getObjectSubObjects (parentObjects, variable) {
      let subObjects = []
      parentObjects.forEach(object => {
        if (object[variable]) {
          subObjects = subObjects.concat(object[variable])
        }
      })
      return subObjects
    }

    _renderObjectItem (object, visibleObjects, level) {
      let subObjects = []
      if (object.type === 'contact') {
        subObjects = object.projects
      } else if (object.type === 'project') {
        subObjects = object.phases
      } else if (object.type === 'phase') {
        subObjects = object.activities
      } else if (object.type === 'activity') {
        subObjects = object.tasks
      }

      // If project, contact, phase dont have any subobjects, hide it from list
      if (
        object.type !== 'task' &&
        object.type !== 'user' &&
        subObjects.length <= 0
      ) {
        return null
      }

      let subObjectElements = null
      const isVisisble = visibleObjects.indexOf(object.type) >= 0
      const bExpand = !!(
        this.data.pageState.expandObjectIds &&
        this.data.pageState.expandObjectIds.indexOf(object.id) >= 0
      )

      // If Parent is visible and not expaneded, then dont render the child item
      if (subObjects && subObjects.length > 0 && (!isVisisble || bExpand)) {
        subObjectElements = subObjects.map(obj =>
          this._renderObjectItem(
            obj,
            visibleObjects,
            isVisisble ? level + 1 : level
          )
        )
      }

      if (!isVisisble) {
        return subObjectElements
      }

      return (
        <div key={object.id} className={`planneritem level${level}`}>
          <div className='content'>{this._renderObject(object, bExpand)}</div>
          {bExpand && <div className='subcontent'>{subObjectElements}</div>}
        </div>
      )
    }

    _renderObject (object, bExpand) {
      const { pageState } = this.data
      const viewMode = pageState.viewMode
      const visibleHours = pageState.visibleHours || []
      const key = `${object.type}-${object.id}`

      // If object has noSubObjects and sub object is visible then show Expand
      let showExpand = false
      if (
        object.noSubObjects &&
        object.noSubObjects > 0 &&
        object.subType &&
        pageState.visibleObjects.indexOf(object.subType) !== -1
      ) {
        showExpand = true
      }

      if (object.type === 'contact') {
        return (
          <PlannerContact
            key={key}
            contact={object}
            viewMode={viewMode}
            showExpand={showExpand}
            isExpand={bExpand}
            onToggleExpand={this._onToggleExpand}
          />
        )
      } else if (object.type === 'project') {
        return (
          <PlannerProject
            key={key}
            project={object}
            viewMode={viewMode}
            showExpand={showExpand}
            isExpand={bExpand}
            onToggleExpand={this._onToggleExpand}
          />
        )
      } else if (object.type === 'phase') {
        return (
          <PlannerPhase
            key={key}
            phase={object}
            viewMode={viewMode}
            showExpand={showExpand}
            onEdit={this._onEditPhase.bind(this, object)}
            isExpand={bExpand}
            onToggleExpand={this._onToggleExpand}
          />
        )
      } else if (object.type === 'activity') {
        return (
          <PlannerActivity
            key={key}
            activity={object}
            viewMode={viewMode}
            showExpand={showExpand}
            onEdit={this._onEditActivity.bind(this, object)}
            isExpand={bExpand}
            onToggleExpand={this._onToggleExpand}
          />
        )
      } else if (object.type === 'task') {
        return (
          <PlannerTask
            key={key}
            task={object}
            viewMode={viewMode}
            showExpand={showExpand}
            onEdit={this._onEditTask.bind(this, object, false)}
            onChangeHour={this._onChangeHour.bind(this, object)}
            isExpand={bExpand}
            onToggleExpand={this._onToggleExpand}
            getTaskHours={this._getTaskHours.bind(this, object.id)}
            showAvailableInfo={visibleHours.indexOf('available') >= 0}
            showPlannedInfo={visibleHours.indexOf('planned') >= 0}
          />
        )
      } else if (object.type === 'user') {
        return (
          <PlannerUser
            key={key}
            user={object}
            viewMode={viewMode}
            getTaskHours={this._getTaskHours.bind(this)}
            showAvailableInfo={visibleHours.indexOf('available') >= 0}
            showPlannedInfo={visibleHours.indexOf('planned') >= 0}
          />
        )
      }

      return null
    }

    _renderDialogConfirm () {
      return (
        <DialogConfirm
          ref={this._references.uploadErrorDialog}
          type='error'
          showOk
          title='plannerv2.task.errorDocTitle'
          subtitle='plannerv2.task.errorDocSubtitle'
          message='plannerv2.task.errorDocMessage'
        />
      )
    }

    _calculateDayItems () {
      this.data.weekItems = []
      this.data.dayItems = []

      const { fromDate, toDate, pageState } = this.data

      if (pageState.viewMode === 'week') {
        this.data.weekItems = this._getRenderWeeks(fromDate, toDate)
      } else {
        this.data.dayItems = this._getRenderDays(fromDate, toDate)
      }
    }

    _calculateFromAndToDates () {
      const { fromDate, pageState } = this.data
      let toDate = null
      const contentWidth = 1600 // Maximum screensize to support is 2000
      let maxNoItems = Math.floor(contentWidth / 32)

      if (pageState.viewMode === 'week') {
        maxNoItems = Math.floor(contentWidth / 124) * 7
      }

      toDate = DateUtil.addDaysToDate(fromDate, maxNoItems)

      return { fromDate, toDate }
    }

    _getRenderDays (fromDate, toDate) {
      const dayItems = []
      const currDate = new Date()

      if (fromDate && toDate && fromDate < toDate) {
        let date = new Date(fromDate)
        while (date < toDate) {
          dayItems.push({
            date: new Date(date),
            dayNo: date.getDate(),
            month: date.getMonth(),
            year: date.getFullYear(),
            title: DateUtil.getDisplayWeekDay(date).substr(0, 2),
            isWeekEnd: DateUtil.getDisplayIsWeekEnd(date),
            isToday: DateUtil.isDateEqual(date, currDate)
          })

          date = DateUtil.addDaysToDate(date, 1)
        }
      }

      return dayItems
    }

    _getRenderWeeks () {
      const weekMap = {}
      const currDate = new Date()
      const currWeekNo = DateUtil.getDisplayWeek(currDate)
      const currYear = currDate.getFullYear()
      const { fromDate, toDate } = this.data
      const weekNos = []

      if (fromDate && toDate && fromDate < toDate) {
        let date = new Date(fromDate)
        while (date < toDate) {
          let weekNo = DateUtil.getDisplayWeek(date)
          if (date.getDay() === 0) {
            const prevDate = new Date(date)
            prevDate.setDate(prevDate.getDate() - 1)
            weekNo = DateUtil.getDisplayWeek(prevDate)
          }

          if (!weekMap[weekNo]) {
            weekNos.push(weekNo)
            const year = date.getFullYear()
            weekMap[weekNo] = {
              year,
              weekNo,
              isActive: year === currYear && weekNo === currWeekNo,
              items: []
            }
          }

          weekMap[weekNo].items.push({
            date: new Date(date),
            title: DateUtil.getDisplayWeekDay(date).substr(0, 1),
            isActive: false,
            isWeekEnd: DateUtil.getDisplayIsWeekEnd(date)
          })

          date = DateUtil.addDaysToDate(date, 1)
        }
      }

      const weekItems = []
      weekNos.forEach(weekNo => {
        weekItems.push(weekMap[weekNo])
      })

      return weekItems
    }

    _getDefaultKanbanInfo () {
      return {
        todo: 0,
        inProgress: 0,
        completed: 0,
        countTotal: 0,
        countTodo: 0,
        countInProgress: 0,
        countCompleted: 0
      }
    }

    _calculateKanbanProgress (info) {
      info.countTotal =
        info.countTodo + info.countInProgress + info.countCompleted

      if (info.countTotal > 0) {
        info.todo = (info.countTodo / info.countTotal) * 100 || 0
        info.inProgress = (info.countInProgress / info.countTotal) * 100 || 0
        info.completed = (info.countCompleted / info.countTotal) * 100 || 0
      }
    }

    _calculateResult (context) {
      const { pageState } = this.data
      if (pageState.isUserMode) {
        this._calculateResultForUserMode(context)
        return
      }

      const activityMap = {}
      const phaseMap = {}
      const projectMap = {}
      const contactMap = {}
      const userScheduleMap = {}
      const userTimecardMap = {}
      const userTaskMap = {}

      context.activities.forEach(obj => {
        if (obj.id) {
          obj.type = 'activity'
          obj.tasks = []
          obj.noSubObjects = 0
          obj.subType = 'task'
          obj.dayItems = []
          obj.plannedHours = 0
          obj.kanbanInfo = this._getDefaultKanbanInfo()

          activityMap[obj.id.toString()] = obj
        }
      })

      context.projects.forEach(obj => {
        if (obj.id) {
          obj.type = 'project'
          obj.phases = []
          obj.noSubObjects = 0
          obj.subType = 'phase'
          obj.dayItems = []
          obj.kanbanInfo = this._getDefaultKanbanInfo()
          projectMap[obj.id.toString()] = obj

          if (obj.budget && obj.budget.phases) {
            obj.budget.phases.forEach((phase, idx) => {
              const phaseNo = idx + 1

              if (!phase.title) {
                phase.title = `${IntlUtil.convertMessage(
                  'planner.planner.phaseLabel'
                )} ${idx + 1}`
              }

              phase.type = 'phase'
              phase.phaseNo = phaseNo
              phase.projectId = obj.id
              phase.id = `${obj.id}-${phaseNo}`
              phase.activities = []
              phase.noSubObjects = 0
              phase.subType = 'activity'
              phase.dayItems = []
              phase.kanbanInfo = this._getDefaultKanbanInfo()

              phaseMap[phase.id] = phase
            })
          }
        }
      })

      context.contacts.forEach(obj => {
        if (obj.id) {
          obj.type = 'contact'
          obj.projects = []
          obj.noSubObjects = 0
          obj.subType = 'project'
          obj.dayItems = []
          obj.kanbanInfo = this._getDefaultKanbanInfo()
          contactMap[obj.id.toString()] = obj
        }
      })

      context.schedules.forEach(schedule => {
        if (!userScheduleMap[schedule.userId]) {
          userScheduleMap[schedule.userId] = []
        }

        userScheduleMap[schedule.userId].push(schedule)
      })

      context.timecards.forEach(timecard => {
        if (!userTimecardMap[timecard.userId]) {
          userTimecardMap[timecard.userId] = {}
        }

        const dateKey = this._getKeyFromDate(timecard.createdOn)
        if (!userTimecardMap[timecard.userId][dateKey]) {
          userTimecardMap[timecard.userId][dateKey] = timecard.timeTaken
        } else {
          userTimecardMap[timecard.userId][dateKey] += timecard.timeTaken
        }
      })

      context.tasks.forEach(task => {
        if (task.userId) {
          if (!userTaskMap[task.userId]) {
            userTaskMap[task.userId] = {}
          }

          task.schedules.forEach(schedule => {
            const dateKey = this._getKeyFromDate(schedule.date)
            if (!userTaskMap[task.userId][dateKey]) {
              userTaskMap[task.userId][dateKey] = schedule.noHours || 0
            } else {
              userTaskMap[task.userId][dateKey] += schedule.noHours
            }
          })
        }
      })

      if (context.tasks && context.tasks.length > 0) {
        context.tasks.forEach(task => {
          task.plannedHours = 0
          task.trackedHours = task.timeSpent
            ? parseFloat((task.timeSpent / 60).toFixed(1))
            : 0
          task.kanbanInfo = this._getDefaultKanbanInfo()

          const scheduleMap = {}
          task.schedules.forEach(schedule => {
            const date = new Date(schedule.date)
            const key = this._getKeyFromDate(date)

            task.plannedHours += schedule.noHours || 0

            if (scheduleMap[key]) {
              scheduleMap[key].noHours += schedule.noHours || 0
            } else {
              schedule.noHours = schedule.noHours || 0
              scheduleMap[key] = schedule
            }
          })

          this._getRangesFromSchedule(context, task, scheduleMap)
          if (task.activityId) {
            task.type = 'task'
            task.noSubObjects = 0
            task.subType = ''

            const activity = activityMap[task.activityId.toString()]
            activity.tasks.push(task)
            activity.noSubObjects += 1
            activity.plannedHours += task.plannedHours || 0

            if (task.completed) {
              task.kanbanInfo.completed = 100
              task.kanbanInfo.countCompleted++

              activity.kanbanInfo.countCompleted++
            } else if (task.timeSpent) {
              task.kanbanInfo.inProgress = 100
              task.kanbanInfo.countInProgress++

              activity.kanbanInfo.countInProgress++
            } else {
              task.kanbanInfo.todo = 100
              task.kanbanInfo.countTodo++

              activity.kanbanInfo.countTodo++
            }
          }

          // Calculate scheduled hours
          if (task.userId) {
            const userScheduleOptions = userScheduleMap[task.userId]
            if (task.dayItems && task.dayItems.length > 0) {
              task.dayItems.forEach(dayItem => {
                const dateKey = this._getKeyFromDate(dayItem.date)
                dayItem.availableHours = this._getTimeFromScheduleOptionFromList(
                  userScheduleOptions,
                  dayItem.date
                )

                if (userTimecardMap[task.userId]) {
                  dayItem.availableHours -=
                    userTimecardMap[task.userId][dateKey] || 0
                }

                if (userTaskMap[task.userId]) {
                  const scheduleNoHours = userTaskMap[task.userId][dateKey] || 0
                  dayItem.availableHours -= scheduleNoHours * 60
                }

                dayItem.availableHours =
                  dayItem.availableHours > 0 ? dayItem.availableHours / 60 : 0
              })
            }
          }
        })
      }

      Object.keys(activityMap).forEach(activityId => {
        const activity = activityMap[activityId]
        this._calculateKanbanProgress(activity.kanbanInfo)

        if (activity.projectId) {
          this._mergeSchedules(context, activity, activity.tasks)

          const phase =
            phaseMap[`${activity.projectId.toString()}-${activity.phaseNo}`]
          if (!phase.activities) {
            phase.activities = []
          }
          phase.activities.push(activity)
          phase.noSubObjects += 1
          phase.kanbanInfo.countCompleted +=
            activity.kanbanInfo.countCompleted || 0
          phase.kanbanInfo.countInProgress +=
            activity.kanbanInfo.countInProgress || 0
          phase.kanbanInfo.countTodo += activity.kanbanInfo.countTodo || 0
        }
      })

      Object.keys(phaseMap).forEach(phaseId => {
        const phase = phaseMap[phaseId]
        this._calculateKanbanProgress(phase.kanbanInfo)

        if (phase.projectId) {
          phase.dayItems = this._getRangesFromDate(
            context,
            phase.fromDate,
            phase.toDate
          )

          const project = projectMap[phase.projectId.toString()]
          project.phases.push(phase)
          project.noSubObjects += 1
          project.kanbanInfo.countCompleted +=
            phase.kanbanInfo.countCompleted || 0
          project.kanbanInfo.countInProgress +=
            phase.kanbanInfo.countInProgress || 0
          project.kanbanInfo.countTodo += phase.kanbanInfo.countTodo || 0
        }
      })

      Object.keys(projectMap).forEach(projectId => {
        const project = projectMap[projectId]
        this._calculateKanbanProgress(project.kanbanInfo)

        if (project.contactId) {
          project.dayItems = this._getRangesFromDate(
            context,
            project.startDate,
            project.endDate
          )

          const contact = contactMap[project.contactId.toString()]
          contact.projects.push(project)
          contact.noSubObjects += 1
          contact.kanbanInfo.countCompleted +=
            project.kanbanInfo.countCompleted || 0
          contact.kanbanInfo.countInProgress +=
            project.kanbanInfo.countInProgress || 0
          contact.kanbanInfo.countTodo += project.kanbanInfo.countTodo || 0
        }
      })

      // Form objects and overall kanban info
      const overallKanbanInfo = this._getDefaultKanbanInfo()

      const objects = []
      Object.keys(contactMap).forEach(contactId => {
        const contact = contactMap[contactId]
        this._calculateKanbanProgress(contact.kanbanInfo)

        overallKanbanInfo.countCompleted +=
          contact.kanbanInfo.countCompleted || 0
        overallKanbanInfo.countInProgress +=
          contact.kanbanInfo.countInProgress || 0
        overallKanbanInfo.countTodo += contact.kanbanInfo.countTodo || 0
        overallKanbanInfo.countTotal += contact.kanbanInfo.countTotal || 0

        // For contact no need to show the schedules
        this._mergeSchedules(context, contact, [])

        objects.push(contact)
      })

      this.data.objects = objects
      this.data.kanbanInfo = overallKanbanInfo
    }

    _calculateResultForUserMode (context) {
      const userMap = {}
      const overallKanbanInfo = this._getDefaultKanbanInfo()

      context.tasks.forEach(task => {
        const { user: taskUser, ...taskInfo } = task

        if (taskUser) {
          let user = userMap[taskUser.id]
          if (!user) {
            user = {
              ...taskUser,
              type: 'user',
              subType: '',
              noSubObjects: 0,
              tasks: [],
              kanbanInfo: this._getDefaultKanbanInfo()
            }
          }

          user.tasks.push(taskInfo)
          userMap[taskUser.id] = user

          user.kanbanInfo.countTotal++
          overallKanbanInfo.countTotal++

          if (task.completed) {
            user.kanbanInfo.countCompleted++
            overallKanbanInfo.countCompleted++
          } else if (task.timeSpent) {
            user.kanbanInfo.countInProgress++
            overallKanbanInfo.countInProgress++
          } else {
            user.kanbanInfo.countTodo++
            overallKanbanInfo.countTodo++
          }
        }
      })

      this.data.objects = []
      this.data.kanbanInfo = overallKanbanInfo

      const userScheduleMap = {}
      const userTimecardMap = {}
      const userTaskMap = {}

      context.schedules.forEach(schedule => {
        if (!userScheduleMap[schedule.userId]) {
          userScheduleMap[schedule.userId] = []
        }

        userScheduleMap[schedule.userId].push(schedule)
      })

      context.timecards.forEach(timecard => {
        if (!userTimecardMap[timecard.userId]) {
          userTimecardMap[timecard.userId] = {}
        }

        const dateKey = this._getKeyFromDate(timecard.createdOn)
        if (!userTimecardMap[timecard.userId][dateKey]) {
          userTimecardMap[timecard.userId][dateKey] = timecard.timeTaken
        } else {
          userTimecardMap[timecard.userId][dateKey] += timecard.timeTaken
        }
      })

      context.tasks.forEach(task => {
        if (task.userId) {
          if (!userTaskMap[task.userId]) {
            userTaskMap[task.userId] = {}
          }

          task.schedules.forEach(schedule => {
            const dateKey = this._getKeyFromDate(schedule.date)
            if (!userTaskMap[task.userId][dateKey]) {
              userTaskMap[task.userId][dateKey] = schedule.noHours || 0
            } else {
              userTaskMap[task.userId][dateKey] += schedule.noHours
            }
          })
        }
      })

      for (const userId in userMap) {
        const user = userMap[userId]

        this._mergeSchedules(context, user, user.tasks || [])
        this._calculateKanbanProgress(user.kanbanInfo)

        const { tasks, schedules, ...userInfo } = user

        if (userInfo.dayItems) {
          userInfo.dayItems.forEach(dayItem => {
            const dateKey = this._getKeyFromDate(dayItem.date)
            const userScheduleOptions = userScheduleMap[userId]
            dayItem.availableHours = this._getTimeFromScheduleOptionFromList(
              userScheduleOptions,
              dayItem.date
            )

            if (userTimecardMap[userId]) {
              dayItem.availableHours -= userTimecardMap[userId][dateKey] || 0
            }

            if (userTaskMap[userId]) {
              const scheduleNoHours = userTaskMap[userId][dateKey] || 0
              dayItem.availableHours -= scheduleNoHours * 60
            }

            dayItem.availableHours =
              dayItem.availableHours > 0 ? dayItem.availableHours / 60 : 0
          })
        }

        this.data.objects.push(userInfo)
      }
    }

    _getTimeFromScheduleOptionFromList (
      scheduleOptions,
      clientDate,
      clientTimezoneOffset
    ) {
      let scheduledTime = 0

      if (scheduleOptions) {
        for (let idx = 0; idx < scheduleOptions.length; idx++) {
          scheduledTime = this._getTimeFromScheduleOption(
            scheduleOptions[idx],
            clientDate,
            clientTimezoneOffset
          )
          if (scheduledTime !== -1) {
            break
          } else {
            scheduledTime = 0
          }
        }
      }

      return scheduledTime
    }

    _getTimeFromScheduleOption (
      scheduleOption,
      clientDate,
      _clientTimezoneOffset
    ) {
      let bSchedule = false

      if (scheduleOption.fromDate || scheduleOption.toDate) {
        const fromDate = scheduleOption.fromDate
          ? new Date(scheduleOption.fromDate)
          : null
        if (fromDate) {
          fromDate.setHours(0, 0, 0, 0)
        }

        const toDate = scheduleOption.toDate
          ? new Date(scheduleOption.toDate)
          : null
        if (toDate) {
          toDate.setHours(23, 59, 59, 0)
        }

        const dayDateTime = new Date(clientDate).getTime()

        if (fromDate && toDate) {
          if (
            dayDateTime >= fromDate.getTime() &&
            dayDateTime <= toDate.getTime()
          ) {
            bSchedule = true
          }
        } else if (fromDate) {
          if (dayDateTime >= fromDate.getTime()) {
            bSchedule = true
          }
        } else if (toDate) {
          if (dayDateTime <= toDate.getTime()) {
            bSchedule = true
          }
        }
      } else {
        bSchedule = true
      }

      if (bSchedule) {
        return this._getUserScheduleOption(scheduleOption.schedules, clientDate)
      } else {
        return -1
      }
    }

    _getUserScheduleOption (scheduleOptions, dayDate) {
      let weekNo = DateUtil.getDisplayWeek(dayDate)
      let weekDayNo = dayDate.getDay() // Sunday will be 0
      if (weekDayNo === 0) {
        weekDayNo = 7

        const prevDate = new Date(dayDate.getTime() - 1 * 24 * 60 * 60 * 1000)
        weekNo = DateUtil.getDisplayWeek(prevDate) // Sunday will be shown in previous week
      }

      // Schedule option days always start for Odd Week first
      let dayNo = 1
      if (weekNo % 2 === 1) {
        dayNo = weekDayNo
      } else {
        dayNo = weekDayNo + 7
      }

      const scheduleOptionObj = _.find(scheduleOptions, scheduleOption => {
        return scheduleOption.dayNo === dayNo ? scheduleOption : null
      })

      return scheduleOptionObj ? scheduleOptionObj.timeTaken : 0
    }

    _mergeSchedules (context, object, subObjects) {
      // For kanban mode no need to process dates
      const { pageState } = this.data
      if (pageState.viewMode === 'kanban') {
        return
      }

      const scheduleMap = {}

      subObjects.forEach(subObject => {
        subObject.schedules.forEach(schedule => {
          const date = schedule.date
          const key = this._getKeyFromDate(date)

          if (scheduleMap[key]) {
            scheduleMap[key].noHours += schedule.noHours || 0
          } else {
            scheduleMap[key] = {
              date: schedule.date,
              noHours: schedule.noHours || 0
            }
          }
        })
      })

      object.schedules = Object.values(scheduleMap)

      this._getRangesFromSchedule(context, object, scheduleMap)
    }

    _getRangesFromSchedule (context, object, scheduleMap) {
      const { fromDate, toDate, pageState } = this.data

      let contextProject = null

      context.projects.map(project => {
        contextProject = project
      })

      const startDate = contextProject.startDate
      const endDate = contextProject.endDate

      // For kanban mode no need to process dates
      if (pageState.viewMode === 'kanban') {
        return
      }

      const tempDate = new Date(fromDate)
      let scheduledHours = 0

      if (object.schedules) {
        object.schedules.forEach(schedule => {
          scheduledHours += schedule.noHours
        })
      }

      object.dayItems = []
      while (tempDate < toDate) {
        const dayItem = {
          date: new Date(tempDate),
          scheduledHours: scheduledHours,
          noHours: 0,
          availableHours: 0,
          position: '', // start || middle || end || active
          disabled: false,
          isDueDate: false,
          showMarker: false,
          startDate: startDate,
          endDate: endDate,
          duration: object.duration || 0
        }

        if (object.dueDate) {
          object.dueDate = new Date(object.dueDate)

          if (tempDate > object.dueDate) {
            dayItem.disabled = true
          }

          if (DateUtil.isDateEqual(tempDate, object.dueDate)) {
            dayItem.isDueDate = true
          }
        }

        const prevDate = new Date(tempDate)
        prevDate.setDate(prevDate.getDate() - 1)

        const nextDate = new Date(tempDate)
        nextDate.setDate(nextDate.getDate() + 1)

        const currSchedule = scheduleMap[this._getKeyFromDate(tempDate)]
        const prevSchedule = scheduleMap[this._getKeyFromDate(prevDate)]
        const nextSchedule = scheduleMap[this._getKeyFromDate(nextDate)]

        if (currSchedule) {
          dayItem.noHours = currSchedule.noHours

          if (prevSchedule && nextSchedule) {
            dayItem.position = 'middle'
          } else if (prevSchedule) {
            dayItem.position = 'end'
          } else if (nextSchedule) {
            dayItem.position = 'start'
          } else {
            dayItem.position = 'active'
          }
        }

        object.dayItems.push(dayItem)

        tempDate.setDate(tempDate.getDate() + 1)
      }
    }

    _getRangesFromDate (_context, objectFromDate, objectToDate) {
      const dayItems = []

      const { fromDate, toDate, pageState } = this.data

      // For kanban mode no need to process dates
      if (pageState.viewMode === 'kanban') {
        return
      }

      const tempDate = new Date(fromDate)
      const objectFromTS = objectFromDate
        ? new Date(objectFromDate).setHours(0, 0, 0, 0)
        : 0
      const objectToTS = objectToDate
        ? new Date(objectToDate).setHours(0, 0, 0, 0)
        : 0

      while (tempDate < toDate) {
        const dayItem = {
          date: new Date(tempDate),
          noHours: 0,
          availableHours: 0,
          position: '' // start || middle || end || active
        }

        const tempDateTS = new Date(tempDate).setHours(0, 0, 0, 0)
        if (objectFromTS && objectToTS) {
          if (tempDateTS === objectFromTS && objectFromTS === objectToTS) {
            dayItem.position = 'active'
          } else if (tempDateTS === objectFromTS) {
            dayItem.position = 'start'
          } else if (tempDateTS === objectToTS) {
            dayItem.position = 'end'
          } else if (tempDateTS > objectFromTS && tempDateTS < objectToTS) {
            dayItem.position = 'middle'
          }
        } else if (objectFromTS && tempDateTS === objectFromTS) {
          dayItem.position = 'active'
        }

        dayItems.push(dayItem)

        tempDate.setDate(tempDate.getDate() + 1)
      }

      return dayItems
    }

    _getKeyFromDate (date) {
      date = new Date(date)
      return `${date.getDate()}-${date.getMonth()}-${date.getFullYear()}`
    }

    _onNavigate (incr) {
      let { fromDate, context, pageState } = this.data
      fromDate = fromDate || new Date()
      fromDate.setDate(
        fromDate.getDate() + incr * (pageState.viewMode === 'week' ? 7 : 1)
      )

      const { toDate } = this._calculateFromAndToDates()

      this.data.fromDate = fromDate
      this.data.toDate = toDate

      this._calculateDayItems()
      this._calculateResult(context)
      this.setState({ dataDate: new Date() })
    }

    _onChangeMode (viewMode) {
      const { context, pageState, taskFromDate, taskToDate } = this.data
      const prevViewMode = pageState.viewMode

      if (prevViewMode !== viewMode) {
        this.data.pageState.viewMode = viewMode

        let { fromDate, toDate } = this._calculateFromAndToDates()

        if (viewMode === 'week') {
          fromDate = DateUtil.getMondayByDate(fromDate)
        }

        this.data.fromDate = fromDate
        this.data.toDate = toDate

        // If previsous mode is kanban, clear the fitler
        if (prevViewMode === 'kanban' && taskFromDate && taskToDate) {
          this.data.taskFromDate = null
          this.data.taskToDate = null

          this._reloadData()
        } else {
          this._calculateDayItems()
          this._calculateResult(context)

          this.setState({ dataDate: new Date() })
        }

        this._updatePageState(pageState)
      }
    }

    _onChangeDate (fromDate, toDate) {
      const { context, pageState } = this.data
      if (pageState.viewMode === 'kanban') {
        this.data.taskFromDate = fromDate
        this.data.taskToDate = toDate

        this._reloadData()
      } else {
        this.data.fromDate = fromDate

        const { toDate } = this._calculateFromAndToDates()

        this.data.toDate = toDate

        this._calculateDayItems()
        this._calculateResult(context)

        this.setState({ dataDate: new Date() })
      }
    }

    _onToggleActionHours (action) {
      const { pageState } = this.data

      if (pageState.visibleHours) {
        if (pageState.visibleHours.indexOf(action.ident) >= 0) {
          _.remove(pageState.visibleHours, ident => ident === action.ident)
        } else {
          pageState.visibleHours.push(action.ident)
        }

        this.setState({ dataDate: new Date() })

        this._updatePageState(pageState)
      }
    }

    _onToggleKanban (action) {
      const viewMode = action.ident === 'kanban' ? 'kanban' : 'day'
      const { pageState } = this.data

      if (pageState.viewMode !== viewMode) {
        if (action.ident === 'kanban') {
          pageState.viewMode = 'kanban'

          this.setState({ dataDate: new Date() })
        } else {
          this._onChangeMode('day')
        }

        this._updatePageState(pageState)
      }
    }

    _onChangeVisibleObjects (action) {
      const { pageState, context, objects } = this.data

      if (action.ident === 'user') {
        if (!pageState.isUserMode) {
          pageState.isUserMode = true
          pageState.visibleObjects = ['user']
        } else {
          pageState.isUserMode = false
          pageState.visibleObjects = this._getAllVisibleObjects()
        }

        this._calculateResult(context)
      } else if (pageState.visibleObjects.indexOf(action.ident) >= 0) {
        if (pageState.visibleObjects.length > 1) {
          _.remove(pageState.visibleObjects, ident => ident === action.ident)
        }
      } else {
        if (pageState.isUserMode) {
          pageState.visibleObjects = []
          pageState.isUserMode = false

          this._calculateResult(context)
        }

        pageState.visibleObjects.push(action.ident)
      }

      // Task: #1163 - Onchanging visible objects, auto expand all the visible objects
      const visibleObjects = pageState.visibleObjects
      const expandObjectIds = pageState.expandObjectIds || []
      const expandIds = []

      // Page planner
      if (objects !== undefined) {
        let expandLevel = 0
        if (visibleObjects.indexOf('project') !== -1) {
          expandLevel = 1
        }
        if (visibleObjects.indexOf('phase') !== -1) {
          expandLevel = 2
        }
        if (visibleObjects.indexOf('activity') !== -1) {
          expandLevel = 3
        }
        if (visibleObjects.indexOf('task') !== -1) {
          expandLevel = 4
        }

        let noExpanded = 0
        objects.forEach(object => {
          noExpanded += 1

          // Contact object - Only expand first 2 objects
          if (expandLevel > 0 && noExpanded <= 2) {
            expandIds.push(object.id)

            // Project object
            if (expandLevel > 1 && object.projects) {
              object.projects.forEach(project => {
                expandIds.push(project.id)

                // Phase object
                if (expandLevel > 2 && project.phases) {
                  project.phases.forEach(phase => {
                    expandIds.push(phase.id)

                    // Activity object
                    if (expandLevel > 3 && phase.activities) {
                      phase.activities.forEach(activity => {
                        expandIds.push(activity.id)
                      })
                    }
                  })
                }
              })
            }
          }
        })
      } else if (
        visibleObjects.indexOf('task') !== -1 &&
        this.data.activities
      ) {
        // Task Page - if task is selected, expand activities
        this.data.activities.forEach(activity => {
          expandIds.push(activity.id)
        })
      }

      if (expandIds.length > 0) {
        expandIds.forEach(expandId => {
          _.remove(expandObjectIds, itemId => itemId === expandId)
          expandObjectIds.push(expandId)
        })
      }

      pageState.expandObjectIds = expandObjectIds

      this.data.pageState = pageState
      this.data.pageState.pageNo = 1
      this.setState({ dataDate: new Date() })

      this._updatePageState(pageState)
    }

    _onToggleExpand = (bExpand, objectId) => {
      if (!this.data.pageState.expandObjectIds) {
        this.data.pageState.expandObjectIds = []
      }

      if (bExpand) {
        this.data.pageState.expandObjectIds.push(objectId)
      } else {
        _.remove(this.data.pageState.expandObjectIds, id => id === objectId)
      }

      this.setState({ dataDate: new Date() })

      this._updatePageState(this.data.pageState)
    }

    _renderPlannerActionGroup () {
      const { pageState } = this.data
      if (!pageState.showToolbarActions) {
        return null
      }

      const actions = [
        {
          ident: 'phase',
          title: 'plannerv2.task.plannerActionPhase'
        },
        {
          ident: 'activity',
          title: 'plannerv2.task.plannerActionActivity'
        },
        {
          ident: 'task',
          title: 'plannerv2.task.plannerActionTask'
        },
        {
          ident: 'user',
          title: 'plannerv2.task.plannerActionUser'
        }
      ]

      if (this._pageIdent === 'planner') {
        actions.unshift(
          {
            ident: 'contact',
            title: 'plannerv2.task.plannerActionClient'
          },
          {
            ident: 'project',
            title: 'plannerv2.task.plannerActionProject'
          }
        )
      }

      return (
        <div className='gridactionbar'>
          <PlannerActionGroup
            title='plannerv2.task.plannerActionTitle'
            actions={actions}
            activeActions={pageState.visibleObjects}
            onChange={this._onChangeVisibleObjects.bind(this)}
          />
          <PlannerActionGroup
            title='plannerv2.task.plannerActionHours'
            actions={[
              {
                ident: 'available',
                title: 'plannerv2.task.plannerActionAvailable'
              },
              {
                ident: 'planned',
                title: 'plannerv2.task.plannerActionPlanned'
              }
            ]}
            activeActions={pageState.visibleHours || []}
            onChange={this._onToggleActionHours.bind(this)}
          />
          <PlannerActionGroup
            title='plannerv2.task.plannerActionView'
            actions={[
              {
                ident: 'day',
                title: 'plannerv2.task.plannerActionCalendar'
              },
              {
                ident: 'kanban',
                title: 'plannerv2.task.plannerActionKanban'
              }
            ]}
            activeActions={[pageState.viewMode === 'kanban' ? 'kanban' : 'day']}
            onChange={this._onToggleKanban.bind(this)}
          />
        </div>
      )
    }
    /* Planner functions - END */

    /* RightSidebar Functions - START */
    _renderRightSidebarTask (projects, skillOptions, task) {
      return (
        <SidebarTask
          ref={this._references.sidebarTask}
          projects={projects || []}
          skillOptions={skillOptions || []}
          task={task}
          user={app.loggedInUserTeam}
          onClose={this._onRightSidebarClose.bind(this)}
          onSaveEdit={this._onUpdateTaskInfo.bind(this)}
          onSaveAdd={this._onCreateTask.bind(this)}
          onDelete={this._onDeleteTask.bind(this)}
          getTaskHours={this._getTaskHours.bind(this)}
          getProjectBudgetItems={this._getProjectBudgetItems.bind(this)}
          onSearchUsers={this._onSearchUsers.bind(this)}
          onUpdateDocuments={this._onUpdateDocuments.bind(this)}
          onCreateComment={this._onCreateComment}
          onUploadError={this._onUploadError}
        />
      )
    }

    _renderRightSidebarPlanner () {
      const { currentEditObject, context } = this.data

      if (
        (!currentEditObject && this._showRightSidebar) ||
        (currentEditObject && currentEditObject.type === 'task')
      ) {
        return this._renderRightSidebarTask(
          context.projectOptions,
          context.skillOptions,
          currentEditObject
        )
      }

      if (currentEditObject && currentEditObject.type === 'phase') {
        return (
          <SidebarPhase
            phase={currentEditObject}
            onClose={this._onRightSidebarClose.bind(this)}
            onSave={this._onUpdatePhaseInfo.bind(this)}
          />
        )
      } else if (currentEditObject && currentEditObject.type === 'activity') {
        return (
          <SidebarActivity
            activity={currentEditObject}
            onClose={this._onRightSidebarClose.bind(this)}
            onSave={this._onUpdateActivityInfo.bind(this)}
          />
        )
      }

      return null
    }

    _onRightSidebarClose () {
      this.data.currentEditObject = null
      this.hideRightSidebar()
    }

    _onChangeHour (task, date, noHours) {
      if (!task.id) {
        return
      }

      if (this._timerTaskHourUpdate) {
        window.clearTimeout(this._timerTaskHourUpdate)
        this._timerTaskHourUpdate = null
      }

      const { context } = this.data
      const schedule = _.find(task.schedules, obj =>
        DateUtil.isDateEqual(obj.date, date)
      )

      if (schedule) {
        if (noHours === 0) {
          _.remove(task.schedules, obj => DateUtil.isDateEqual(obj.date, date))
        } else {
          schedule.noHours = noHours
        }
      } else if (noHours > 0) {
        task.schedules.push({
          date,
          noHours
        })
      }

      this._calculateResult(context)
      this.setState({ dataDate: new Date() })

      this._timerTaskHourUpdate = window.setTimeout(() => {
        this._onUpdateTaskInfo(task.id, { schedules: task.schedules })
      }, 700)
    }

    _getTaskHours (objectId, userId, date, dueDate, callback) {
      const { context } = this.data
      let toDate = null

      date = new Date(date)
      const fromDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      )
      if (dueDate) {
        toDate = new Date(dueDate)
      } else {
        toDate = new Date(fromDate)
      }
      toDate.setHours(23, 59, 59, 0)
      let availableHours = 0
      let duration = 0
      let totalScheduleHours = 0
      let totalDuration = 0
      let scheduleDate = ''

      if (context && context.tasks) {
        context.tasks.map(task => {
          if (task.id === objectId) {
            task.dayItems.map(item => {
              availableHours = item.availableHours
              duration = item.duration / 60
              totalDuration += task.duration / 60
            })

            task.schedules.map(schedule => {
              totalScheduleHours += schedule.noHours
              scheduleDate = schedule.date
            })
          }
        })
      }

      if (!userId) {
        callback([])
      } else {
        const params = {
          query: {
            fromDate: fromDate,
            toDate: toDate,
            userId: userId,
            type: 'with-activities',
            clientTimezoneOffset: DateUtil.getLocalTimezoneOffset()
          }
        }

        app
          .service('/api/project/plannerschedule')
          .find(params)
          .then(searchRes => {
            const projectMap = {}
            searchRes.data.forEach(activity => {
              if (activity.project) {
                let project = projectMap[activity.projectId]
                if (!project) {
                  project = {
                    id: activity.projectId,
                    title: activity.project.title,
                    noHours: 0,
                    availableHours: availableHours,
                    duration: duration,
                    totalDuration: totalDuration,
                    totalScheduleHours: totalScheduleHours,
                    scheduleDate: scheduleDate
                  }
                }

                project.noHours += activity.noHours
                projectMap[activity.projectId] = project
              }
            })

            const projects = Object.keys(projectMap).map(key => projectMap[key])

            callback(projects)
          })
          .catch(err => {
            app.debug('PlannerPageProvider: _getTaskHours Error', err)
            callback([])
          })
      }
    }

    _onEditPhase (object) {
      this.data.currentEditObject = object

      this.showRightSidebar()
    }

    _onEditActivity (object) {
      this.data.currentEditObject = object

      this.showRightSidebar()
    }
    /* RightSidebar Functions - END */

    /* Realtime Events - START */
    _initTaskEvents () {
      this._eventHandlers.taskCreated = this._eventReloadTask.bind(
        this,
        'Task',
        'Created'
      )
      this._eventHandlers.taskUpdated = this._eventReloadTask.bind(
        this,
        'Task',
        'Updated'
      )
      this._eventHandlers.taskRemoved = this._eventReloadTask.bind(
        this,
        'Task',
        'Removed'
      )
      this._eventHandlers.taskPatched = this._eventReloadTask.bind(
        this,
        'Task',
        'Patched'
      )

      const taskService = app.service('/api/project/plannertask')
      taskService.on('created', this._eventHandlers.taskCreated)
      taskService.on('updated', this._eventHandlers.taskUpdated)
      taskService.on('removed', this._eventHandlers.taskRemoved)
      taskService.on('patched', this._eventHandlers.taskPatched)
    }

    _releaseTaskEvents () {
      const taskService = app.service('/api/project/plannertask')
      taskService.off('created', this._eventHandlers.taskCreated)
      taskService.off('updated', this._eventHandlers.taskUpdated)
      taskService.off('removed', this._eventHandlers.taskRemoved)
      taskService.off('patched', this._eventHandlers.taskPatched)

      this._eventHandlers.taskCreated = null
      this._eventHandlers.taskUpdated = null
      this._eventHandlers.taskRemoved = null
      this._eventHandlers.taskPatched = null
    }

    _initActivityEvents () {
      this._eventHandlers.activityCreated = this._eventReloadActivity.bind(
        this,
        'Activity',
        'Created'
      )
      this._eventHandlers.activityUpdated = this._eventReloadActivity.bind(
        this,
        'Activity',
        'Updated'
      )
      this._eventHandlers.activityRemoved = this._eventReloadActivity.bind(
        this,
        'Activity',
        'Removed'
      )
      this._eventHandlers.activityPatched = this._eventReloadActivity.bind(
        this,
        'Activity',
        'Patched'
      )

      const activityService = app.service('/api/project/budgetitem')
      activityService.on('created', this._eventHandlers.activityCreated)
      activityService.on('updated', this._eventHandlers.activityUpdated)
      activityService.on('removed', this._eventHandlers.activityRemoved)
      activityService.on('patched', this._eventHandlers.activityPatched)
    }

    _releaseActivityEvents () {
      const activityService = app.service('/api/project/budgetitem')
      activityService.off('created', this._eventHandlers.activityCreated)
      activityService.off('updated', this._eventHandlers.activityUpdated)
      activityService.off('removed', this._eventHandlers.activityRemoved)
      activityService.off('patched', this._eventHandlers.activityPatched)

      this._eventHandlers.activityCreated = null
      this._eventHandlers.activityUpdated = null
      this._eventHandlers.activityRemoved = null
      this._eventHandlers.activityPatched = null
    }

    _initProjectEvents () {
      this._eventHandlers.projectCreated = this._eventReloadProject.bind(
        this,
        'Project',
        'Created'
      )
      this._eventHandlers.projectUpdated = this._eventReloadProject.bind(
        this,
        'Project',
        'Updated'
      )
      this._eventHandlers.projectRemoved = this._eventReloadProject.bind(
        this,
        'Project',
        'Removed'
      )
      this._eventHandlers.projectPatched = this._eventReloadProject.bind(
        this,
        'Project',
        'Patched'
      )

      const projectService = app.service('/api/project')
      projectService.on('created', this._eventHandlers.projectCreated)
      projectService.on('updated', this._eventHandlers.projectUpdated)
      projectService.on('removed', this._eventHandlers.projectRemoved)
      projectService.on('patched', this._eventHandlers.projectPatched)
    }

    _releaseProjectEvents () {
      const projectService = app.service('/api/project')
      projectService.off('created', this._eventHandlers.projectCreated)
      projectService.off('updated', this._eventHandlers.projectUpdated)
      projectService.off('removed', this._eventHandlers.projectRemoved)
      projectService.off('patched', this._eventHandlers.projectPatched)

      this._eventHandlers.projectCreated = null
      this._eventHandlers.projectUpdated = null
      this._eventHandlers.projectRemoved = null
      this._eventHandlers.projectPatched = null
    }
    /* Realtime Events - END */
  }

export default PlannerPageProvider
